/* eslint-disable react/jsx-props-no-spreading -- Dropzone requires prop spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@americanexpress/dls-react';
import { IconUpload } from '@americanexpress/dls-icons';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

const UploadZone = ({
  onDrop,
  sumOfDocs,
  isDropZoneDisabled,
  maxBatchSize,
  maxFileSize,
  uploadZoneView,
  isKycView,
  isMultipleAllowed,
  fileTypes,
}) => {
  let isUploadDisabled = isDropZoneDisabled;
  const selectMessageId = isMultipleAllowed ? 'selectFiles' : 'selectSingleFile';
  const selectAriaLabel = isMultipleAllowed ? 'Select files' : 'Select file';
  if (sumOfDocs >= maxBatchSize) {
    isUploadDisabled = false;
  }
  switch (uploadZoneView) {
    case 'Disabled':
      return null;
    case 'ViewOne': {
      const containerClassName = isKycView
        ? 'dnuk-upload-zone-view-one-kyc-container'
        : 'width-full';
      const dropzoneClassName = isKycView
        ? 'flex flex-column flex-align-center dls-white-bg dnuk-upload-zone-view-one-kyc'
        : 'flex flex-column flex-align-center border-dark-dashed radius-container-sm dls-white-bg dnuk-upload-zone-view-one width-full';
      const textWrapClassName = isKycView
        ? 'dnuk-upload-zone-view-one-kyc-text-wrap'
        : 'dnuk-upload-zone-text-wrap';
      const dragMessageId = 'dragAndDropOneKyc';

      return (
        <div className={`${containerClassName} dnuk-upload-zone-responsive-container`}>
          <Dropzone
            onDrop={onDrop}
            maxSize={maxFileSize}
            minSize={1}
            accept={fileTypes}
            disabled={isUploadDisabled}
            multiple={isMultipleAllowed}
            aria-label={`${selectAriaLabel} region`}
          >
            {({ getRootProps, getInputProps }) => (
              <div className={dropzoneClassName} {...getRootProps()}>
                <div className="dnuk-upload-zone-icon-line">
                  <IconUpload
                    size="lg"
                    color={isUploadDisabled ? 'neutral' : 'brand'}
                    title="upload icon"
                  />
                </div>
                <input aria-label="uploadZoneInput" {...getInputProps()} />
                <div className={textWrapClassName}>
                  <div
                    tabIndex={0}
                    data-testid={`${isUploadDisabled}Drag`}
                    className={`${
                      isUploadDisabled ? 'dls-grey-05' : 'dls-black'
                    } text-align-center font-weight-normal dnuk-upload-zone-body-text hidden-xs-down`}
                  >
                    <FormattedMessage id={dragMessageId} />
                  </div>
                  <div
                    tabIndex={0}
                    data-testid={`${isUploadDisabled}Or`}
                    className={`${
                      isUploadDisabled ? 'dls-grey-05' : 'dls-grey-06'
                    } text-align-center font-weight-normal dnuk-upload-zone-body-text hidden-xs-down`}
                  >
                    <FormattedMessage id="orLabel" />
                  </div>
                  <div
                    role="button"
                    aria-label={selectAriaLabel}
                    tabIndex={0}
                    data-testid={`${isUploadDisabled}Select`}
                    className={`${
                      isUploadDisabled
                        ? 'dls-grey-05'
                        : 'dls-bright-blue icon-hover'
                    } text-align-center font-weight-normal dnuk-upload-zone-body-text dnuk-upload-zone-select-link`}
                  >
                    <FormattedMessage id={selectMessageId} />
                  </div>
                </div>
              </div>
            )}
          </Dropzone>
        </div>
      );
    }
    default:
      return (
        <div className="flex flex-justify-center flex-align-center text-align-center dnuk-upload-zone-responsive-container">
          <Dropzone
            onDrop={onDrop}
            maxSize={maxFileSize}
            minSize={1}
            accept={fileTypes}
            disabled={isUploadDisabled}
            multiple={isMultipleAllowed}
            aria-label={`${selectAriaLabel} region`}
          >
            {({ getRootProps, getInputProps }) => (
              <div
                className="flex-column border-dark-dashed pad-b pad-1-t dls-accent-grey-01-bg"
                {...getRootProps()}
              >
                <div>
                  <IconUpload
                    size="lg"
                    color={isUploadDisabled ? 'neutral' : 'brand'}
                    title="upload icon"
                  />
                </div>
                <div className="hidden-xs-down">
                  <input {...getInputProps()} />
                  <FormattedMessage id="dragAndDrop" />
                </div>
                <div className="pad-1-tb hidden-xs-down">
                  <FormattedMessage id="orLabel" />
                </div>
                <div>
                  <Button variant="primary" disabled={isUploadDisabled}>
                    <FormattedMessage id="browseButton" />
                  </Button>
                </div>
              </div>
            )}
          </Dropzone>
        </div>
      );
  }
};

UploadZone.propTypes = {
  onDrop: PropTypes.func.isRequired,
  sumOfDocs: PropTypes.number.isRequired,
  isDropZoneDisabled: PropTypes.bool.isRequired,
  maxFileSize: PropTypes.number.isRequired,
  maxBatchSize: PropTypes.number.isRequired,
  fileTypes: PropTypes.string.isRequired,
  uploadZoneView: PropTypes.string.isRequired,
  isKycView: PropTypes.bool,
  isMultipleAllowed: PropTypes.bool,
};

UploadZone.defaultProps = {
  isKycView: false,
  isMultipleAllowed: true,
};

export default UploadZone;
/* eslint-enable react/jsx-props-no-spreading -- Dropzone requires prop spreading */
